import React, { FC, useMemo, useState, useCallback } from "react"
import { csvTableService } from "../../service/csvTableService"
import { EditCellDialog } from "./EditCellDialog"
import './CsvTableViewer.less'

interface Props {
    sourceCode: string
    editable?: boolean
    onChange?: (updatedSourceCode: string) => void
}

interface CellPosition {
    rowIndex: number
    cellIndex: number
}

const componentData = {
    version: 1
}

export const CsvTableViewer: FC<Props> = ({
    sourceCode,
    editable = false,
    onChange
}) => {
    const rows = useMemo(() => csvTableService.parseCsv(sourceCode), [sourceCode])
    const [editDialog, setEditDialog] = useState<{
        isOpen: boolean
        value: string
        position: CellPosition | null,
        version: number,
    }>({
        isOpen: false,
        value: '',
        position: null,
        version: componentData.version,
    })

    const handleCellClick = useCallback((rowIndex: number, cellIndex: number, cellValue: string) => {
        if (editable) {
            componentData.version += 1
            setEditDialog({
                isOpen: true,
                value: cellValue,
                position: { rowIndex, cellIndex },
                version: componentData.version,
            })
        }
    }, [editable])

    const handleSaveCell = useCallback((newValue: string, position: CellPosition) => {
        if (!onChange || !position) {
            return
        }

        const updatedRows = rows.map((row, rowIdx) => {
            if (rowIdx === position.rowIndex) {
                return {
                    ...row,
                    cells: row.cells.map((cell, cellIdx) => {
                        if (cellIdx === position.cellIndex) {
                            return newValue
                        }
                        return cell
                    })
                }
            }
            return row
        })

        // Reconstruct CSV from updated rows
        const updatedSourceCode = updatedRows.map(row => {
            return row.cells.map(cell => {
                // Escape quotes and wrap in quotes if cell contains comma, quote, or newline
                if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
                    return `"${cell.replace(/"/g, '""')}"`
                }
                return cell
            }).join(',')
        }).join('\n')

        onChange(updatedSourceCode)
        setEditDialog({ isOpen: false, value: '', position: null, version: componentData.version })
    }, [onChange, rows])

    const handleCloseDialog = useCallback(() => {
        setEditDialog({ isOpen: false, value: '', position: null, version: componentData.version })
    }, [])

    if (rows.length === 0) {
        return <div className='csbTableViewer empty'>No data</div>
    }

    const maxColumns = Math.max(...rows.map(row => row.cells.length))

    return (
        <>
            <div className='csvTableViewer'>
                <table>
                    <thead>
                        {rows.filter(row => row.isHeader).map((row, rowIndex) => (
                            <tr key={`header-${rowIndex}`}>
                                {row.cells.map((cell, cellIndex) => (
                                    <th
                                        key={`header-cell-${rowIndex}-${cellIndex}`}
                                        className={editable ? 'editable' : ''}
                                        onClick={() => handleCellClick(rowIndex, cellIndex, cell.trim())}
                                    >
                                        {cell.trim()}
                                    </th>
                                ))}
                                {/* Add empty header cells to fill up to maxColumns */}
                                {Array.from({ length: maxColumns - row.cells.length }).map((_, idx) => (
                                    <th key={`empty-header-${idx}`}></th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {rows.filter(row => !row.isHeader).map((row, rowIndex) => (
                            <tr key={`row-${rowIndex}`}>
                                {row.cells.map((cell, cellIndex) => (
                                    <td
                                        key={`cell-${rowIndex}-${cellIndex}`}
                                        className={editable ? 'editable' : ''}
                                        onClick={() => handleCellClick(rowIndex + 1, cellIndex, cell)}
                                    >
                                        {cell}
                                    </td>
                                ))}
                                {/* Add empty cells to fill up to maxColumns */}
                                {Array.from({ length: maxColumns - row.cells.length }).map((_, idx) => (
                                    <td
                                        key={`empty-cell-${rowIndex}-${idx}`}
                                        className={editable ? 'editable' : ''}
                                        onClick={() => handleCellClick(rowIndex, idx, '')}
                                    >
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <EditCellDialog
                isOpen={editDialog.isOpen}
                initialValue={editDialog.value}
                version={editDialog.version}
                position={editDialog.position}
                onClose={handleCloseDialog}
                onSave={handleSaveCell}
            />
        </>
    )
}
